<template>
  <div class="container-fluid px-4">
    <label>
      *Note: {{ note }}
    </label>

    <BaseTable :rows="skills" :columns="columns" searchable :selectable="false" :loading="loading"
      :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <template #buttons>
        <MaterialButton size="sm" variant="success" @click="updateInterestSkills">
          Update Interest Skills
        </MaterialButton>
      </template>

      <template #actions="{ row }">
        <MaterialButton size="sm" variant="text" @click="assignSkill(row)">
          <i class="material-icons text-lg text-green me-1">check</i>
        </MaterialButton>
      </template>
    </BaseTable>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from "vue";
import { useStore } from "vuex";
import BaseTable from "@/components/Table/Table.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import { useRoute, useRouter } from "vue-router";
import { getLocationId } from "../../../utils/SessionDataHandler";

const store = useStore();
const route = useRoute();
const router = useRouter();

// 📌 Extract params
const _injectedPersonId = inject('editUserPersonId', null);
const personId = _injectedPersonId?.value || Number(route.params.userid);
const locationId = getLocationId();

// 📌 Local state
const loading = ref(true);
const searchTerm = ref("");

// 📌 Vuex Getters
const skills = computed(() => store.getters.allInterestSkills);
const note = computed(() => store.getters.interestSkillsNote);
const pagination = computed(() => store.getters.personInterestSkillsPagination || {});

// 📌 Table Columns
const columns = [
  { key: "name", label: "Skill Name" },
  { key: "description", label: "Description" },
];

// --------------------------------------------------
// 🔹 API FETCH with Pagination & Search
// --------------------------------------------------
const fetchSkills = async (page = 1, pageSize = pagination.value.pageSize ?? 10) => {
  loading.value = true;
  try {
    await store.dispatch("fetchInterestSkills", {
      personId,
      locationId,
      page,
      pageSize,
      search: searchTerm.value,
    });
  } finally {
    loading.value = false;
  }
};

// --------------------------------------------------
// 🔹 Table Event Handlers
// --------------------------------------------------
const onChangePage = async (page) => {
  await fetchSkills(page, pagination.value.pageSize);
};

const onUpdatePageSize = async (pageSize) => {
  await fetchSkills(1, pageSize); // reset to first page
};

const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchSkills(1, pagination.value.pageSize ?? 10);
};

// --------------------------------------------------
// 🔹 Assign Skill + Refresh
// --------------------------------------------------
const assignSkill = async (row) => {
  await store.dispatch("savePersonSkill", {
    personId,
    skillId: row.id,
    locationId,
  });
  await fetchSkills(pagination.value.currentPage ?? 1, pagination.value.pageSize ?? 10);
};

const updateInterestSkills = async () => {
  router.push({ name: "volunteer-signup", params: { userID: personId, locationID: locationId } });
};

// --------------------------------------------------
// 🔹 Lifecycle
// --------------------------------------------------
onMounted(async () => {
  await fetchSkills();
});
</script>

<style scoped>
label {
  display: block;
  margin-bottom: 12px;
  font-weight: 500;
  color: #555;
}
</style>